import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, FileText, CheckCircle2, Gavel, Download, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { SignatureCapture } from '@/components/documents/SignatureCapture';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { sanitizeDocumentContent } from '@/lib/sanitize';

interface JourneyResolutionStepProps {
  stepId: string;
  userId: string;
  resolutionCategory: string;
  resolutionTitle: string;
  autoPopulateFromPreviousStep: boolean;
  previousStepField?: string;
  isCompleted: boolean;
  onComplete: () => void;
  introText?: string;
  completedDocumentId?: string;
  completedGovernanceDocumentId?: string;
}

interface UnaData {
  id: string;
  name: string;
  primary_officer_role: string | null;
}

interface ProfileData {
  full_name: string;
}

interface CompletedDocumentData {
  content_html: string;
  signed_file_url: string | null;
  file_path?: string | null;
  document_title: string;
}

export function JourneyResolutionStep({
  stepId,
  userId,
  resolutionCategory,
  resolutionTitle,
  autoPopulateFromPreviousStep,
  previousStepField,
  isCompleted,
  onComplete,
  introText,
  completedDocumentId,
  completedGovernanceDocumentId,
}: JourneyResolutionStepProps) {
  const [loading, setLoading] = useState(true);
  const [unaData, setUnaData] = useState<UnaData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [previousStepData, setPreviousStepData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  
  // Completed document state
  const [completedDocument, setCompletedDocument] = useState<CompletedDocumentData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // If completed, fetch the completed document
        if (isCompleted) {
          // First try generated_documents if we have the ID
          if (completedDocumentId) {
            const { data: genDoc } = await supabase
              .from('generated_documents')
              .select('custom_content, file_url, file_path, document_name')
              .eq('id', completedDocumentId)
              .single();
            
            if (genDoc?.custom_content) {
              setCompletedDocument({
                content_html: genDoc.custom_content,
                signed_file_url: genDoc.file_url,
                file_path: genDoc.file_path,
                document_title: genDoc.document_name,
              });
              setLoading(false);
              return;
            }
          }
          
          // Fallback to governance_documents
          if (completedGovernanceDocumentId) {
            const { data: govDoc } = await supabase
              .from('governance_documents')
              .select('content_html, signed_file_url, document_title')
              .eq('id', completedGovernanceDocumentId)
              .single();
            
            if (govDoc?.content_html) {
              setCompletedDocument({
                content_html: govDoc.content_html,
                signed_file_url: govDoc.signed_file_url,
                document_title: govDoc.document_title,
              });
              setLoading(false);
              return;
            }
          }
          
          // Last resort: find by title in governance_documents
          const { data: una } = await supabase
            .from('unas')
            .select('id')
            .eq('treasury_secretary_id', userId)
            .maybeSingle();
          
          if (una) {
            const { data: govDoc } = await supabase
              .from('governance_documents')
              .select('content_html, signed_file_url, document_title, id')
              .eq('una_id', una.id)
              .eq('document_title', resolutionTitle)
              .eq('document_type', 'resolution')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (govDoc?.content_html) {
              setCompletedDocument({
                content_html: govDoc.content_html,
                signed_file_url: govDoc.signed_file_url,
                document_title: govDoc.document_title,
              });
            }
          }
          
          setLoading(false);
          return;
        }
        
        // Fetch user's UNA
        const { data: una, error: unaError } = await supabase
          .from('unas')
          .select('id, name, primary_officer_role')
          .eq('treasury_secretary_id', userId)
          .maybeSingle();

        if (unaError) throw unaError;
        if (!una) {
          toast.error('UNA not found. Please complete previous steps first.');
          return;
        }
        setUnaData(una);

        // Fetch user profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;
        setProfileData(profile);

        // If auto-populate is enabled, get previous step data
        if (autoPopulateFromPreviousStep) {
          // Get current step number
          const { data: currentStep } = await supabase
            .from('journey_steps')
            .select('step_number')
            .eq('id', stepId)
            .single();

          if (currentStep) {
            // Get previous step
            const { data: prevStep } = await supabase
              .from('journey_steps')
              .select('id')
              .eq('step_number', currentStep.step_number - 1)
              .eq('is_active', true)
              .single();

            if (prevStep) {
              // Get progress data from previous step
              const { data: prevProgress } = await supabase
                .from('user_journey_step_progress')
                .select('action_data')
                .eq('user_id', userId)
                .eq('step_id', prevStep.id)
                .single();

              if (prevProgress?.action_data) {
                const actionData = prevProgress.action_data as Record<string, any>;
                setPreviousStepData(actionData.form_data || actionData);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load step data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, stepId, autoPopulateFromPreviousStep, isCompleted, completedDocumentId, completedGovernanceDocumentId, resolutionTitle]);

  const getAddress = () => {
    const address = previousStepData.place_of_worship_address || '';
    const cityStateZip = previousStepData.place_of_worship_city_state_zip || '';
    return `${address}${cityStateZip ? ', ' + cityStateZip : ''}`;
  };

  // Format date for signature line
  const getSignatureDateParts = () => {
    const now = new Date();
    const day = format(now, 'd');
    const month = format(now, 'MMMM');
    const year = format(now, 'yy');
    return { day, month, year };
  };

  // Wrap HTML in full ecclesiastical document styling for PDF generation
  const wrapInEcclesiasticalStyles = (bodyHtml: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      background: white;
      margin: 0;
      padding: 0;
    }
    
    h1 { 
      font-size: 18pt; 
      font-weight: bold; 
      margin: 0 0 20pt 0;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    
    h2 { 
      font-size: 13pt; 
      font-weight: bold; 
      margin: 24pt 0 10pt 0;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    
    h3 { 
      font-size: 12pt; 
      font-weight: bold; 
      font-style: italic;
      margin: 18pt 0 8pt 0;
      color: #333;
    }
    
    h4 {
      font-size: 11pt;
      font-weight: bold;
      margin: 16pt 0 6pt 0;
      color: #333;
    }
    
    p { 
      margin: 0 0 14pt 0;
      font-size: 11pt;
      text-align: justify;
    }
    
    li { margin: 8pt 0; }
    ol, ul { margin: 14pt 0; padding-left: 28pt; }
    
    .signature-section {
      margin-top: 40pt;
      padding-top: 24pt;
      border-top: 1px solid #ccc;
    }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
  };

  const handleSignature = async (signatureData: string) => {
    if (!unaData || !profileData) return;

    setShowSignature(false);
    setIsSubmitting(true);

    try {
      const fullAddress = getAddress();
      const currentDate = new Date();
      const { day, month, year } = getSignatureDateParts();
      const officerRole = unaData.primary_officer_role || 'Secretary Treasurer';

      // Create resolution content HTML for governance filing - with signature embedded
      const resolutionContent = `
        <h2>RESOLUTION: ${resolutionTitle}</h2>
        <p><strong>Date Adopted:</strong> ${format(currentDate, 'MMMM d, yyyy')}</p>
        <p><strong>Location:</strong> ${fullAddress}</p>
        
        <h3>WHEREAS</h3>
        <p>The Association operates as a faith-based Convention of Stewards and recognizes private Stewardship Observance as a sacred right and duty;</p>
        
        <h3>WHEREAS</h3>
        <p>${profileData.full_name}, an officer of ${unaData.name}, has submitted a signed declaration designating the property at <strong>${fullAddress}</strong> as a private Center for Stewardship Observance under the Doctrine of Divine Stewardship and the Steward's Creed of this Association;</p>
        
        <h3>WHEREAS</h3>
        <p>The Association affirms that Stewardship Observance need not occur in public temples, but in any place set apart by the conscience and faith of its Members;</p>
        
        <h3>NOW THEREFORE, BE IT RESOLVED:</h3>
        <ol>
          <li>That the location identified above is hereby recognized as a Sanctified Center for Stewardship Observance within this Association.</li>
          <li>That any ordinary and necessary expenses associated with maintaining this space for Stewardship Observance (including but not limited to utilities, repairs, furniture, supplies, and modest improvements) may be considered stewardship-based expenses, and approved at the discretion of the ${officerRole}.</li>
          <li>That this recognition shall remain in effect until revoked by written notice of the Executive Board or upon resignation of the Member.</li>
        </ol>
        
        <div class="signature-section" style="margin-top: 40pt; padding-top: 24pt; border-top: 1px solid #ccc;">
          <p class="signature-date" style="margin-bottom: 8pt; font-style: normal; border-left: none; padding-left: 0;">Signed this <span style="font-weight: bold;">${day}</span> day of <span style="font-weight: bold;">${month}</span>, 20<span style="font-weight: bold;">${year}</span>.</p>
          
          <div style="margin-top: 20pt;">
            <img src="${signatureData}" style="max-width: 3in; height: auto; display: block;" alt="Signature" />
            <div style="border-top: 1px solid #333; width: 3.5in; margin-top: 4pt; padding-top: 4pt;">
              <p style="margin: 0; font-size: 10pt;">${profileData.full_name}, ${officerRole}</p>
              <p style="margin: 0; font-size: 9pt; color: #666;">${unaData.name}</p>
            </div>
          </div>
        </div>
      `;

      const styledResolutionContent = wrapInEcclesiasticalStyles(resolutionContent);

      // Build whereas and resolved clauses for structured storage
      const whereasClauses = [
        'The Association operates as a faith-based Convention of Stewards and recognizes private Stewardship Observance as a sacred right and duty;',
        `${profileData.full_name}, an officer of ${unaData.name}, has submitted a signed declaration designating the property at ${fullAddress} as a private Center for Stewardship Observance under the Doctrine of Divine Stewardship and the Steward's Creed of this Association;`,
        'The Association affirms that Stewardship Observance need not occur in public temples, but in any place set apart by the conscience and faith of its Members;'
      ];

      const resolvedClauses = [
        'That the location identified above is hereby recognized as a Sanctified Center for Stewardship Observance within this Association.',
        `That any ordinary and necessary expenses associated with maintaining this space for Stewardship Observance (including but not limited to utilities, repairs, furniture, supplies, and modest improvements) may be considered stewardship-based expenses, and approved at the discretion of the ${officerRole}.`,
        'That this recognition shall remain in effect until revoked by written notice of the Executive Board or upon resignation of the Member.'
      ];

      // Call edge function - it handles resolution creation, governance filing, journal entry, and step completion
      const { data: result, error: resError } = await supabase.functions.invoke('complete-journey-step-with-resolution', {
        body: {
          userId,
          stepId,
          resolutionData: {
            resolution_title: resolutionTitle,
            resolution_category: resolutionCategory,
            resolution_text: styledResolutionContent,
            place_of_worship_address: fullAddress,
            whereas_clauses: whereasClauses,
            resolved_clauses: resolvedClauses
          }
        }
      });

      if (resError) throw resError;

      console.log('Resolution adopted:', result);
      toast.success('Resolution adopted and filed to governance!');
      onComplete();
    } catch (error) {
      console.error('Error creating resolution:', error);
      toast.error('Failed to adopt resolution');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Helper to handle PDF generation and download
  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to download');
        return;
      }

      // Check if we already have a PDF path
      if (completedDocument?.file_path || completedDocument?.signed_file_url) {
        const { getDocumentSignedUrl } = await import('@/lib/storageUtils');
        const url = await getDocumentSignedUrl(completedDocument);
        if (url) {
          window.open(url, '_blank');
          return;
        }
      }

      // Call edge function to generate PDF
      const body: Record<string, string> = {};
      if (completedDocumentId) {
        body.generatedDocumentId = completedDocumentId;
      } else if (completedGovernanceDocumentId) {
        body.governanceDocumentId = completedGovernanceDocumentId;
      } else {
        toast.error('No document ID available');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ensure-governance-document-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const result = await response.json();
      const pdfUrl = result.file_url || result.signed_file_url;
      
      if (pdfUrl) {
        // The edge function now returns signed URLs directly
        // Update local state with the storage path for future use
        setCompletedDocument(prev => prev ? { ...prev, file_path: pdfUrl, signed_file_url: pdfUrl } : null);
        
        // If it looks like a signed URL (starts with http), open directly
        if (pdfUrl.startsWith('http')) {
          window.open(pdfUrl, '_blank');
        } else {
          // Fallback: generate signed URL from storage path
          const { getDocumentSignedUrl } = await import('@/lib/storageUtils');
          const signedUrl = await getDocumentSignedUrl({ file_path: pdfUrl, file_url: pdfUrl });
          if (signedUrl) {
            window.open(signedUrl, '_blank');
          } else {
            toast.error('Could not generate download URL');
            return;
          }
        }
        toast.success('PDF generated and downloaded');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Extract body content from full HTML for display
  const getDisplayContent = (html: string): string => {
    if (html.includes('<html') || html.includes('<body')) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body?.innerHTML?.trim() || html;
      } catch {
        return html;
      }
    }
    return html;
  };

  if (isCompleted) {
    return (
      <div className="space-y-4">
        {/* Success Header */}
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800">Resolution Adopted</h3>
                <p className="text-sm text-green-700">
                  This resolution has been officially recorded and filed to your UNA Governance.
                </p>
              </div>
            </div>

            {/* Document Preview */}
            {completedDocument?.content_html && (
              <Collapsible open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full justify-between mb-3">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {isPreviewOpen ? 'Hide Document' : 'Show Document'}
                    </span>
                    {isPreviewOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border rounded-md bg-white mb-3 overflow-hidden">
                    <ScrollArea className="h-[60vh]">
                      <div 
                        className="p-6 ecclesiastical-document max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(getDisplayContent(completedDocument.content_html)) }}
                      />
                    </ScrollArea>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              {completedDocument?.content_html && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Full Document
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                className="flex items-center gap-2"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Full Document Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{completedDocument?.document_title || resolutionTitle}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-[70vh]">
              <div 
                className="p-6 ecclesiastical-document max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(getDisplayContent(completedDocument?.content_html || '')) }}
              />
            </ScrollArea>
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                className="flex items-center gap-2"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (!unaData || !profileData) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">
            Unable to load required data. Please ensure previous steps are completed.
          </p>
        </CardContent>
      </Card>
    );
  }

  const fullAddress = getAddress();

  return (
    <div className="space-y-6">
      {/* Intro Text */}
      {introText && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{introText}</p>
        </div>
      )}
      
      {/* Resolution Preview */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">{resolutionTitle}</h3>
          </div>
          
          <div className="border rounded-lg p-6 bg-white ecclesiastical-document">
            <h4>WHEREAS</h4>
            <p>The Association operates as a faith-based Convention of Stewards and recognizes private Stewardship Observance as a sacred right and duty;</p>
            
            <h4>WHEREAS</h4>
            <p><strong>{profileData.full_name}</strong>, an officer of <strong>{unaData.name}</strong>, has submitted a signed declaration designating the property at <strong>{fullAddress || '[Address from previous step]'}</strong> as a private Center for Stewardship Observance under the Doctrine of Divine Stewardship and the Steward's Creed of this Association;</p>
            
            <h4>WHEREAS</h4>
            <p>The Association affirms that Stewardship Observance need not occur in public temples, but in any place set apart by the conscience and faith of its Members;</p>
            
            <h4>NOW THEREFORE, BE IT RESOLVED:</h4>
            <ol className="list-decimal pl-0 ml-0">
              <li className="ml-6">That the location identified above is hereby recognized as a Sanctified Center for Stewardship Observance within this Association.</li>
              <li className="ml-6">That any ordinary and necessary expenses associated with maintaining this space for Stewardship Observance (including but not limited to utilities, repairs, furniture, supplies, and modest improvements) may be considered stewardship-based expenses, and approved at the discretion of the {unaData.primary_officer_role || 'Secretary Treasurer'}.</li>
              <li className="ml-6">That this recognition shall remain in effect until revoked by written notice of the Executive Board or upon resignation of the Member.</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Adopt Button */}
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
          <Gavel className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">What happens when you adopt:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Your signature will be embedded in the document</li>
              <li>The resolution will be officially recorded</li>
              <li>It will be filed to your UNA Governance documents</li>
              <li>A journal entry will document this action</li>
            </ul>
          </div>
        </div>

        <Button
          onClick={() => setShowSignature(true)}
          disabled={isSubmitting}
          className="w-full"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adopting Resolution...
            </>
          ) : (
            <>
              <Gavel className="w-4 h-4 mr-2" />
              Adopt Resolution
            </>
          )}
        </Button>
      </div>

      {/* Signature Modal */}
      {showSignature && (
        <SignatureCapture
          role={unaData.primary_officer_role || "treasury_secretary"}
          signerName={profileData.full_name}
          onSave={handleSignature}
          onCancel={() => setShowSignature(false)}
          isLoading={isSubmitting}
        />
      )}
    </div>
  );
}
